import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { Title, Text, Button } from '@tremor/react';
import { SparklesIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { generateTrArtifact, getTrArtifact } from '../../Requests/TendencyReportRequests';
import { useArtifact } from './useArtifact';
import UpgradedLoader from '../UpgradedLoader';

/** Step 2: the general trends report to read BEFORE data collection. */
function TrReportTab({ projectUuid, analysisUuid }) {
    const { t } = useTranslation();
    const get = useCallback(() => getTrArtifact(projectUuid, analysisUuid, 'tr_report'), [projectUuid, analysisUuid]);
    const gen = useCallback(() => generateTrArtifact(projectUuid, analysisUuid, 'tr_report'), [projectUuid, analysisUuid]);
    const { status, result, loading, generate } = useArtifact({ get, generate: gen });
    const markdown = result?.markdown ?? '';
    const sources = Array.isArray(result?.sources) ? result.sources : [];

    return (
        <div data-testid="tr-report-tab">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                    <DocumentTextIcon className="h-6 w-6 text-iavisus-100" />
                    <Title className="text-lg font-semibold">{t('TendencyReport.Report.Title')}</Title>
                </div>
                {status !== 'processing' && (
                    <Button icon={SparklesIcon} onClick={generate} className="bg-iavisus-900">
                        {status === 'completed' ? t('TendencyReport.Regenerate') : t('TendencyReport.Generate')}
                    </Button>
                )}
            </div>
            <Text className="mb-4">{t('TendencyReport.Report.Description')}</Text>

            {(loading || status === 'processing') && <UpgradedLoader />}
            {status === 'failed' && <Text className="text-red-600">{t('TendencyReport.Error')}</Text>}
            {status === 'not_started' && !loading && <Text className="text-gray-500">{t('TendencyReport.Report.Empty')}</Text>}

            {status === 'completed' && markdown && (
                <>
                    <article className="prose prose-sm max-w-none prose-headings:text-iavisus-100">
                        <ReactMarkdown>{markdown}</ReactMarkdown>
                    </article>
                    {sources.length > 0 && (
                        <div className="mt-6 border-t pt-4" data-testid="tr-report-sources">
                            <Text className="font-bold mb-2">{t('TendencyReport.Report.Sources')}</Text>
                            <ul className="list-disc pl-5 space-y-1 text-sm">
                                {sources.map((s) => (
                                    <li key={s.url}>
                                        <a href={s.url} target="_blank" rel="noreferrer" className="text-iavisus-100 hover:underline break-all">
                                            {s.title || s.url}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default TrReportTab;
